import React, { Fragment, useEffect, useState, useRef, useMemo } from "react";
import { Image } from "lib";
import { useDispatch, useSelector } from "react-redux";
import option from "../../assets/images/option.svg";
import "./index.scss";
import {
  recursiveRead,
  setDeleteSaveData,
  setDeleteShowData,
  setShowRecursiveForm,
  setShowDataPreview,
  setAssociateRequired,
  setCurrentFormType,
  setRecursiveFormData,
  clearRecursiveFormData,
} from "redux/recursiveReducer";
import PropTypes from "prop-types";

const PaymentRecursiveDataPreview = ({
  recursiveRead,
  recursiveTrigger,
  data,
  item,
  handleEditChange,
}) => {
  const scrollToRef = useRef(null);

  const search = window.location.search;
  const query = new URLSearchParams(search);
  const transaction_id = useMemo(() => {
    return query.get("transaction_id");
  }, [search, query]);

  const dispatch = useDispatch();
  const requestedDocs = useSelector(
    (state) => state.requestedDocReducer.requestedDocs
  );
  const disableDelete = requestedDocs?.requestedFlow;
  const primaryUser = useSelector((state) => state.userInfo?.primaryUser);
  const showRecursiveForm = useSelector(
    (state) => state.recursive?.showRecursiveForm
  );
  const currentFormType = useSelector(
    (state) => state.recursive.currentFormType
  );
  const isEditRecursiveForm = useSelector(
    (state) => state.recursive?.isEditRecursiveForm
  );
  const loggedInUserInfo = useSelector(
    (state) => state.loggedInUserInfo?.userAuthInfo
  );
  const maxActiveGroup = useSelector((state) => state.userInfo.maxActiveGroup);
  const maxActiveSection = useSelector(
    (state) => state.userInfo.maxActiveSection
  );
  const saveData = useSelector((state) => state.recursive.saveData);
  const showData = useSelector((state) => state.recursive.showData);
  const [isClicked, setIsClicked] = useState(false);
  const [selectedId, setSelectedId] = useState();

  const ref = useOutsideClick(() => setIsClicked(false));

  const requestedId = React.useMemo(
    () => new URLSearchParams(window.location.search).get("requestedId"),
    []
  );

  const handleOptionChange = (index) => {
    setSelectedId(index);
    setIsClicked(!isClicked);
  };

  const handleDelete = (index) => {
    dispatch(setDeleteShowData(index));
    dispatch(setDeleteSaveData(index));
    dispatch(clearRecursiveFormData());
    setIsClicked(false);
  };

  useEffect(() => {
    if (recursiveTrigger && saveData.length === 0) {
      dispatch(setAssociateRequired(false));
      dispatch(setCurrentFormType("applicant"));
    }
  }, [recursiveTrigger, saveData, dispatch]);

  useEffect(() => {
    if (showData.length === 0) {
      dispatch(setShowDataPreview(false));
      dispatch(setShowRecursiveForm(true));
    }
  }, [showData, dispatch]);

  const handleEdit = (index) => {
    setIsClicked(false);
    handleEditChange(index);
  };

  const titleCase = (s) =>
    s
      .toString()
      .replaceAll("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const mapValues = (item) => {
    if (Array.isArray(item)) {
      return item.map((_item) => _item["value"]).join(",");
    } else if (typeof item === "object") {
      return item?.original_name;
    } else {
      return item;
    }
  };

  useEffect(() => {
    const isMatch =
      item?.length &&
      item.some(
        (entry, index) =>
          entry === "transaction_id" && item[index + 1] === transaction_id
      );

    if (isMatch) {
      const element = document.querySelector(".preview");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [transaction_id, item]);

  const getPreviewProps = (item) => {
    const className = `preview ${
      transaction_id === item?.transaction_id
        ? "highlightBoxColderAndShadow"
        : ""
    }`;
    const ref = transaction_id === item?.transaction_id ? scrollToRef : null;
    const key = `preview-${item?.id}`;

    return { className, ref, key };
  };

  const getOptionButtonDisabledState = () =>
    isEditRecursiveForm || (primaryUser?.created_by != null && !requestedId);

  const getOptionList = (item, index) => {
    const disabledState = primaryUser?.created_by != null && !requestedId;

    return (
      <div className="option_list shadow-sm rounded-0 border-0 px-1 mb-2 mx-0 bg-white ">
        <button
          disabled={disabledState}
          onClick={() => handleEdit(index)}
          className="option_list_item px-2 bg-hover-grey rounded-0 bg-active-grey "
        >
          Edit
        </button>
        {transaction_id === item?.transaction_id ? null : (
          <button
            disabled={disabledState}
            onClick={() => handleDelete(index)}
            className={`option_list_item px-2 bg-hover-grey rounded-0 bg-active-grey `}
          >
            Delete
          </button>
        )}
      </div>
    );
  };

  const getPreviewItems = (item) =>
    Object.entries(item).map((_item, _index) => {
      return (
        <Fragment key={`fragment-${item?.id ?? _index}`}>
          <PaymentRecursiveDataPreview item={_item} />
        </Fragment>
      );
    });

  const renderPreviewItem = (label, value) => (
    <div className="preview_item">
      <span className="preview_item_label">{titleCase(label)}</span>
      <span className="preview_item_value">{mapValues(value)}</span>
    </div>
  );

  const renderPreview = (item, index) => {
    const { className, ref, key } = getPreviewProps(item);

    return (
      <div className={className} ref={ref} key={key}>
        {Object.keys(item).length && getPreviewItems(item)}
        {!showRecursiveForm &&
          (transaction_id === item?.transaction_id ||
            !item?.transaction_id) && (
            // (true) && (
            <button
              ref={ref}
              className="option_button"
              onClick={() => handleOptionChange(index)}
              disabled={getOptionButtonDisabledState()}
            >
              <Image imageSrc={option} className={`modal_trigger`} />
              {isClicked && selectedId === index && getOptionList(item, index)}
            </button>
          )}
      </div>
    );
  };

  return (
    <>
      {item?.length && item[1] !== true && renderPreviewItem(item[0], item[1])}
      <div className="form-container">
        {data ? data.map(renderPreview) : null}
      </div>
    </>
  );
};

PaymentRecursiveDataPreview.propTypes = {
  recursiveRead: PropTypes.bool,
  recursiveTrigger: PropTypes.bool,
  data: PropTypes.array,
  item: PropTypes.array,
  handleEditChange: PropTypes.func,
  transaction_id: PropTypes.string,
};

export default PaymentRecursiveDataPreview;

const useOutsideClick = (callback) => {
  const ref = useRef();
  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.className !== "modal_trigger") {
        callback();
      }
    };
    document.addEventListener("click", handleClick);
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [ref]);
  return ref;
};
